import {
    EInputFieldType,
    IInputFieldConfig,
    IInputFieldOption,
} from '../shared/types/input-field.types'
import { IAddTeacherRoleOption } from '../shared/types/id-creation.types'
import {
    EAddAdminField,
    EAddStudentField,
    EAddTeacherField,
    EAddTeacherRole,
    EUserGender,
} from '@app/core/enum/school-site.enums'
import { EI_INPUT_FIELD_CONSTANTS } from '@app/shared/constants/ei-input-field.constants'
import type { TInputFieldTextFilter } from '../shared/types/input-field.types'

export const USER_FIELD_MAX_LENGTH = {
    NAME: 255,
    ROLL_NUMBER: 20,
    ENROLLMENT_NUMBER: 100,
    SECTION: 20,
    EMAIL: 255,
    TITLE: 25,
    DESIGNATION: 25,
} as const

/** Shared validation copy for single-creation and bulk-upload flows (NAV-9805). */
export const USER_FIELD_VALIDATION = {
    messages: {
        maxLength: (fieldLabel: string, max: number): string =>
            `${fieldLabel} must be at most ${max} characters.`,
        nameInvalidChars: (fieldLabel: string): string =>
            `${fieldLabel} can only contain letters, spaces, dots, and hyphens.`,
        sectionInvalidChars: 'Section can only contain letters, numbers, spaces, and hyphens.',
        rollInvalidChars: 'Roll Number can only contain digits (0–9).',
        rollCannotBeZero: 'Roll Number cannot be 0.',
        enrollmentInvalidChars: 'Enrollment Number can only contain letters and numbers.',
        emailInvalid: 'Please enter a valid email address.',
        firstNameRequired: 'First Name is required',
        sectionRequired: 'Section is required',
    },
} as const

const FIRST_NAME_TEXT_FILTER: TInputFieldTextFilter = 'namePart'
const OPTIONAL_NAME_PART_TEXT_FILTER: TInputFieldTextFilter = 'namePart'
const ROLL_TEXT_FILTER: TInputFieldTextFilter = 'digitsOnly'
const ENROLLMENT_TEXT_FILTER: TInputFieldTextFilter = 'alphanumericOnly'

// Shared user constants
export const ADD_USER_GENDER_OPTIONS: readonly IInputFieldOption[] = [
    { label: 'Prefer not to say', value: EUserGender.PreferNotToSay },
    { label: 'Female', value: EUserGender.Female },
    { label: 'Male', value: EUserGender.Male },
]

// Navigation/editing keys allowed for section “Other” keyboard handling
export const SECTION_OTHER_ALLOWED_KEYS = EI_INPUT_FIELD_CONSTANTS.ALLOWED_PHONE_KEYS

// Teacher Constants
const ADD_TEACHER_FORM_FIELDS: Record<EAddTeacherField, IInputFieldConfig> = {
    [EAddTeacherField.FirstName]: {
        label: 'First Name',
        placeholder: 'Enter First Name',
        required: true,
        type: EInputFieldType.Text,
        textFilter: FIRST_NAME_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddTeacherField.MiddleName]: {
        label: 'Middle Name',
        placeholder: 'Enter Middle Name',
        required: false,
        type: EInputFieldType.Text,
        textFilter: OPTIONAL_NAME_PART_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddTeacherField.LastName]: {
        label: 'Last Name',
        placeholder: 'Enter Last Name',
        required: false,
        type: EInputFieldType.Text,
        textFilter: OPTIONAL_NAME_PART_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddTeacherField.DateOfBirth]: {
        label: 'Date of Birth',
        placeholder: 'DD-MMM-YYYY',
        required: false,
        type: EInputFieldType.Date,
    },
    [EAddTeacherField.Gender]: {
        label: 'Gender',
        placeholder: 'Select Gender',
        required: false,
        type: EInputFieldType.Select,
        options: ADD_USER_GENDER_OPTIONS,
    },
    [EAddTeacherField.Phone]: {
        label: 'Phone Number',
        placeholder: 'Enter Phone Number',
        required: true,
        type: EInputFieldType.Phone,
    },
    [EAddTeacherField.Email]: {
        label: 'Email ID',
        placeholder: 'Enter Email ID',
        required: true,
        type: EInputFieldType.Email,
        maxLength: USER_FIELD_MAX_LENGTH.EMAIL,
    },
    [EAddTeacherField.Title]: {
        label: 'Title',
        placeholder: 'Add Title',
        type: EInputFieldType.Select,
        options: [],
        allowCreateNew: true,
        maxLength: USER_FIELD_MAX_LENGTH.TITLE,
    },
    [EAddTeacherField.Designation]: {
        label: 'Designation',
        placeholder: 'Select Designation',
        type: EInputFieldType.Select,
        options: [],
        allowCreateNew: true,
        maxLength: USER_FIELD_MAX_LENGTH.DESIGNATION,
    },
}

const ADD_ADMIN_FORM_FIELDS: Record<EAddAdminField, IInputFieldConfig> = {
    [EAddAdminField.FirstName]: {
        label: 'First Name',
        placeholder: 'Enter First Name',
        required: true,
        type: EInputFieldType.Text,
        textFilter: FIRST_NAME_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddAdminField.MiddleName]: {
        label: 'Middle Name',
        placeholder: 'Enter Middle Name',
        required: false,
        type: EInputFieldType.Text,
        textFilter: OPTIONAL_NAME_PART_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddAdminField.LastName]: {
        label: 'Last Name',
        placeholder: 'Enter Last Name',
        required: false,
        type: EInputFieldType.Text,
        textFilter: OPTIONAL_NAME_PART_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddAdminField.Gender]: {
        label: 'Gender',
        placeholder: 'Select Gender',
        required: false,
        type: EInputFieldType.Select,
        options: ADD_USER_GENDER_OPTIONS,
    },
    [EAddAdminField.Phone]: {
        label: 'Phone Number',
        placeholder: 'Enter Phone Number',
        required: true,
        type: EInputFieldType.Phone,
    },
    [EAddAdminField.Title]: {
        label: 'Add Title',
        placeholder: 'Add Title',
        type: EInputFieldType.Select,
        options: [],
        allowCreateNew: true,
        maxLength: USER_FIELD_MAX_LENGTH.TITLE,
    },
    [EAddAdminField.DateOfBirth]: {
        label: 'Date of Birth',
        placeholder: 'DD-MMM-YYYY',
        required: false,
        type: EInputFieldType.Date,
    },
    [EAddAdminField.Email]: {
        label: 'Email ID',
        placeholder: 'Enter Email ID',
        required: true,
        type: EInputFieldType.Email,
        maxLength: USER_FIELD_MAX_LENGTH.EMAIL,
    },
}

const ADD_TEACHER_ROLE_OPTIONS: readonly IAddTeacherRoleOption[] = [
    {
        id: EAddTeacherRole.Teacher,
        label: 'Assign Teacher',
        description: 'Can access students of their own class',
    },
    {
        id: EAddTeacherRole.Admin,
        label: 'Assign Admin',
        description: 'Can access designated classes',
    },
    {
        id: EAddTeacherRole.SuperAdmin,
        label: 'Super Admin',
        description: 'Can manage school settings and access all students, manage subscriptions etc',
        isFullWidth: true,
    },
]

export const SECTION_OTHER_OPTION_VALUE = '__section_other__'
export const SECTION_OTHER_LABEL = 'Other'

/** Minimum age in years for teacher date of birth (calendar max date and validation). */
export const TEACHER_DOB_MIN_AGE_YEARS = 18

export const PHONE_LENGTH_INDIA = { min: 10, max: 10 } as const

/** API payload field keys for add-teacher (shared shape with add-admin). */
export const ADD_TEACHER_PAYLOAD_KEYS = {
    USER_TYPE: 'user_type',
    USER_TYPE_TEACHER: 'teacher',
    NAME: 'name',
    NAME_FIRST: 'first',
    NAME_MIDDLE: 'middle',
    NAME_LAST: 'last',
    GENDER: 'gender',
    CONTACT_DETAILS: 'contact_details',
    CONTACT_TYPE: 'type',
    CONTACT_TYPE_PRIMARY: 'primary',
    CONTACT_EMAIL: 'email',
    CONTACT_COUNTRY_CODE: 'country_code',
    CONTACT_MOBILE_NUMBER: 'mobile_number',
    CONTACT_EMAIL_CONSENT: 'email_consent',
    CONTACT_MOBILE_CONSENT: 'mobile_consent',
    DATE_OF_BIRTH: 'date_of_birth',
    ORGANISATION_CODE: 'organisation_code',
    GROUP_ITEMS: 'group_items',
    GROUP_MAPPING: 'group_mapping',
    CLASS: 'class',
    SECTION: 'section',
    SUBJECT_ID: 'subject_id',
    ROLE: 'role',
    USER_ADDITIONAL_INFO: 'user_additional_info',
    TITLE: 'title',
    DESIGNATION: 'designation',
} as const

export const ADD_ADMIN_PAYLOAD_KEYS = {
    ...ADD_TEACHER_PAYLOAD_KEYS,
    USER_TYPE_SCHOOL_ADMIN: 'school_admin',
} as const

export const ADD_STUDENT_PAYLOAD_KEYS = {
    USER_TYPE: ADD_TEACHER_PAYLOAD_KEYS.USER_TYPE,
    NAME: ADD_TEACHER_PAYLOAD_KEYS.NAME,
    NAME_FIRST: ADD_TEACHER_PAYLOAD_KEYS.NAME_FIRST,
    NAME_MIDDLE: ADD_TEACHER_PAYLOAD_KEYS.NAME_MIDDLE,
    NAME_LAST: ADD_TEACHER_PAYLOAD_KEYS.NAME_LAST,
    GENDER: ADD_TEACHER_PAYLOAD_KEYS.GENDER,
    DATE_OF_BIRTH: ADD_TEACHER_PAYLOAD_KEYS.DATE_OF_BIRTH,
    ORGANISATION_CODE: ADD_TEACHER_PAYLOAD_KEYS.ORGANISATION_CODE,
    SECTION: ADD_TEACHER_PAYLOAD_KEYS.SECTION,
    GRADE: 'grade',
    ENROLLMENT_NUMBER: 'enrollment_number',
    ROLL_NUMBER: 'roll_number',
} as const

export const ADD_TEACHER_DIALOG_CONSTANTS = {
    DIALOG: {
        WIDTH: '640px',
        MAX_WIDTH: '90vw',
        PANEL_CLASS: 'add-teacher-dialog-panel',
        BACKDROP_CLASS: 'common-dialog-backdrop',
    },
    ICONS: {
        CLOSE: 'close',
        CHECK: 'check',
        MAPPING: 'menu_book',
        DELETE: 'delete_outline',
        ADD: 'add',
    },
    SECTIONS: {
        ASSIGN_ROLE_TITLE: 'Assign Role',
        MAPPING_TITLE: 'Class - Subject Mapping',
    },
    FORM_FIELDS: ADD_TEACHER_FORM_FIELDS,
    ROLE_OPTIONS: ADD_TEACHER_ROLE_OPTIONS,
    MAPPING: {
        ROW_ID_PREFIX: 'mapping-row-',
        SUBJECT_LABEL: 'Subject',
        CLASS_LABEL: 'Class',
        SECTION_LABEL: 'Section',
        SECTION_OTHER_PLACEHOLDER: 'Type section name',
        SELECT_CLASS_FIRST_TOOLTIP: 'Please select a class first',
        DUPLICATE_MAPPING: 'This class, subject and section combination is already added',
    },
    DROPDOWN: {
        CREATE_NEW_TITLE: 'Create New Title',
        CREATE_NEW_DESIGNATION: 'Create New Designation',
    },
    VALIDATION: {
        DATE_MUST_BE_AT_LEAST_EIGHTEEN_YEARS_AGO: 'Date of birth must be at least 18 years ago',
        FIELD_REQUIRED: (fieldLabel: string): string => `${fieldLabel} is required`,
        FIELD_MAX_LENGTH: (max: number, fieldLabel: string): string =>
            USER_FIELD_VALIDATION.messages.maxLength(fieldLabel, max),
        EMAIL_INVALID: 'Please enter a valid email address',
        PHONE_INVALID: 'Please enter a valid phone number',
        PHONE_ONLY_DIGITS: 'Phone number must contain only digits',
        PHONE_MIN_LENGTH: (min: number): string => `Phone number must be at least ${min} digits`,
        PHONE_MAX_LENGTH: (max: number): string =>
            `Phone number must be no more than ${max} digits`,
    },
    SUCCESS: {
        DEFAULT_TEACHER_NAME: 'Teacher',
        DEFAULT_SCHOOL_NAME: 'Delhi Public School',
        MESSAGE_TEMPLATE: (fullName: string, schoolName: string): string =>
            `${fullName} has been successfully added <br> as a teacher to ${schoolName}.`,
    },
    DEFAULT_COUNTRY_CODE: 'in',
} as const

export const ADD_ADMIN_DIALOG_CONSTANTS = {
    DIALOG: {
        WIDTH: '640px',
        MAX_WIDTH: '90vw',
        PANEL_CLASS: 'add-admin-dialog-panel',
        BACKDROP_CLASS: 'common-dialog-backdrop',
    },
    ICONS: {
        CLOSE: 'close',
        CHECK: 'check',
    },
    SECTIONS: {
        ASSIGN_ROLE_TITLE: 'Assign Role',
    },
    FORM_FIELDS: ADD_ADMIN_FORM_FIELDS,
    ROLE_OPTIONS: ADD_TEACHER_ROLE_OPTIONS,
    DROPDOWN: {
        CREATE_NEW_TITLE: 'Create New Title',
    },
    VALIDATION: {
        DATE_MUST_BE_AT_LEAST_EIGHTEEN_YEARS_AGO: 'Date of birth must be at least 18 years ago',
        FIELD_REQUIRED: (fieldLabel: string): string => `${fieldLabel} is required`,
        FIELD_MAX_LENGTH: (max: number, fieldLabel: string): string =>
            USER_FIELD_VALIDATION.messages.maxLength(fieldLabel, max),
        EMAIL_INVALID: 'Please enter a valid email address',
        PHONE_INVALID: 'Please enter a valid phone number',
        PHONE_ONLY_DIGITS: 'Phone number must contain only digits',
        PHONE_MIN_LENGTH: (min: number): string => `Phone number must be at least ${min} digits`,
        PHONE_MAX_LENGTH: (max: number): string =>
            `Phone number must be no more than ${max} digits`,
    },
    SUCCESS: {
        DEFAULT_ADMIN_NAME: 'Admin',
        DEFAULT_SCHOOL_NAME: 'Delhi Public School',
        MESSAGE_TEMPLATE: (fullName: string, schoolName: string): string =>
            `${fullName} has been successfully added <br> as an admin to ${schoolName}.`,
    },
    DEFAULT_COUNTRY_CODE: 'in',
} as const

const ADD_STUDENT_FORM_FIELDS: Record<EAddStudentField, IInputFieldConfig> = {
    [EAddStudentField.FirstName]: {
        label: 'First Name',
        placeholder: 'First Name',
        required: true,
        type: EInputFieldType.Text,
        textFilter: FIRST_NAME_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddStudentField.MiddleName]: {
        label: 'Middle Name',
        placeholder: 'Middle Name',
        required: false,
        type: EInputFieldType.Text,
        textFilter: OPTIONAL_NAME_PART_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddStudentField.LastName]: {
        label: 'Last Name',
        placeholder: 'Last Name',
        required: false,
        type: EInputFieldType.Text,
        textFilter: OPTIONAL_NAME_PART_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.NAME,
    },
    [EAddStudentField.Class]: {
        label: 'Choose Class',
        placeholder: 'Choose Class*',
        required: true,
        type: EInputFieldType.Select,
        options: [],
    },
    [EAddStudentField.Section]: {
        label: 'Choose Section',
        placeholder: 'Choose Section*',
        required: true,
        type: EInputFieldType.Select,
        options: [],
    },
    [EAddStudentField.DateOfBirth]: {
        label: 'Date of Birth',
        placeholder: 'DD-MMM-YYYY',
        required: false,
        type: EInputFieldType.Date,
    },
    [EAddStudentField.Gender]: {
        label: 'Choose Gender',
        placeholder: 'Choose Gender',
        required: false,
        type: EInputFieldType.Select,
        options: ADD_USER_GENDER_OPTIONS,
    },
    [EAddStudentField.EnrollmentNumber]: {
        label: 'Enrollment Number',
        placeholder: 'Enrollment Number',
        type: EInputFieldType.Text,
        textFilter: ENROLLMENT_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.ENROLLMENT_NUMBER,
    },
    [EAddStudentField.RollNo]: {
        label: 'Roll No. (school)',
        placeholder: 'Roll No. (school)',
        type: EInputFieldType.Text,
        textFilter: ROLL_TEXT_FILTER,
        maxLength: USER_FIELD_MAX_LENGTH.ROLL_NUMBER,
    },
}

export const ADD_STUDENT_DIALOG_CONSTANTS = {
    HELP_TEXT: {
        DOB_OR_ENROLLMENT:
            'Include Enrollment Number or Date of Birth — or both. At least one is required to generate student ID.',
    },
    VALIDATION: {
        DATE_MUST_BE_AT_LEAST_THREE_YEARS_AGO: 'Date of birth must be at least 3 years ago',
        FIELD_REQUIRED: (fieldLabel: string): string => `${fieldLabel} is required`,
        FIELD_MAX_LENGTH: (max: number, fieldLabel: string): string =>
            USER_FIELD_VALIDATION.messages.maxLength(fieldLabel, max),
        DOB_OR_ENROLLMENT_REQUIRED: 'Please enter either Date of Birth or Enrollment Number.',
    },
    DIALOG: {
        WIDTH: '640px',
        MAX_WIDTH: '90vw',
        PANEL_CLASS: 'add-student-dialog-panel',
        BACKDROP_CLASS: 'common-dialog-backdrop',
    },
    ICONS: {
        CLOSE: 'close',
    },
    FORM_FIELDS: ADD_STUDENT_FORM_FIELDS,
    SUCCESS: {
        DEFAULT_STUDENT_NAME: 'Student',
        DEFAULT_SCHOOL_NAME: 'Delhi Public School',
        MESSAGE_WITH_CREDENTIALS: (
            fullName: string,
            classSection: string,
            schoolName: string,
        ): string =>
            classSection
                ? `<strong>${fullName}</strong> has been added to class ${classSection},<br> ${schoolName} successfully.`
                : `<strong>${fullName}</strong> has been added to <br> ${schoolName} successfully.`,
    },
} as const
